import { formatShortDate } from "@clipboard-health/date-time";
import { PromotionBanner } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { ExtraWorkedTimeRequestStatus } from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";

import { IconButton } from "../../../components/IconButton";

interface ExtraWorkedTimeRequestBannerProps {
  status: ExtraWorkedTimeRequestStatus.APPROVED | ExtraWorkedTimeRequestStatus.DECLINED;
  workplaceName: string;
  shiftDate: Date;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  onClick?: () => void;
  onDismiss?: () => void;
}

function getBannerContent(params: {
  isApproved: boolean;
  isVerified: boolean;
  isFullShiftPayment?: boolean;
  workplaceName: string;
  shiftDate: Date;
}): { title: string; subtitle: string } {
  const { isApproved, isVerified, isFullShiftPayment, workplaceName, shiftDate } = params;

  const isFullShiftPaymentRequest = isFullShiftPayment ?? !isVerified;
  const formattedDate = formatShortDate(shiftDate);

  if (isApproved && isFullShiftPaymentRequest) {
    return {
      title: "Good news! Your full shift payment was approved.",
      subtitle: `${workplaceName} approved your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  if (isApproved) {
    return {
      title: "Good news! Your pay adjustment was approved.",
      subtitle: `${workplaceName} approved your pay adjustment request for your shift on ${formattedDate}.`,
    };
  }

  if (isFullShiftPaymentRequest) {
    return {
      title: "Your full shift payment was declined.",
      subtitle: `${workplaceName} declined your full shift payment for your shift on ${formattedDate}.`,
    };
  }

  return {
    title: "Your pay adjustment was declined.",
    subtitle: `${workplaceName} declined your pay adjustment request for your shift on ${formattedDate}.`,
  };
}

export function ExtraWorkedTimeRequestBanner(props: ExtraWorkedTimeRequestBannerProps) {
  const { status, workplaceName, shiftDate, isVerified, isFullShiftPayment, onClick, onDismiss } =
    props;

  const isApproved = status === ExtraWorkedTimeRequestStatus.APPROVED;

  const { title, subtitle } = getBannerContent({
    isApproved,
    isVerified,
    isFullShiftPayment,
    workplaceName,
    shiftDate,
  });

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <PromotionBanner
        title={title}
        subtitle={subtitle}
        icon={
          isApproved ? "clock-success-with-dollar-colored" : "clock-error-with-exclamation-colored"
        }
        borderVariant="none"
        backgroundColor={(theme) =>
          isApproved ? theme.palette.payment!.background : theme.palette.intent!.error.gradient!
        }
        titleColor={(theme) =>
          isApproved ? theme.palette.payment!.text : theme.palette.intent!.error.text
        }
        sx={(theme) => ({
          width: "100%",
          height: "100%",
          boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.08)",
          // Add right padding to make room for the dismiss button
          ...(onDismiss && { paddingRight: theme.spacing(10) }),
          "& *": {
            whiteSpace: "normal",
            wordWrap: "break-word",
          },
          "& .MuiTypography-body1": {
            fontSize: theme.typography.h5.fontSize,
            lineHeight: theme.typography.h5.lineHeight,
          },
          "& .MuiTypography-body2": {
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
          },
        })}
        onClick={onClick}
      />
      {onDismiss && (
        <IconButton
          iconType="close"
          variant="ghost"
          size="small"
          sx={(theme) => ({
            position: "absolute",
            top: theme.spacing(3),
            right: theme.spacing(3),
          })}
          onClick={(event) => {
            event.stopPropagation();
            onDismiss();
          }}
        />
      )}
    </Box>
  );
}
